import { useEffect, useRef, useState, type FormEvent } from "react";
import { createPortal } from "react-dom";
import { submitReport, type ReportReason, type ReportTargetType } from "../apiClient.js";
import { AsyncButton } from "./AsyncButton.js";
import { useT, type TranslationKey } from "../lib/i18n/store.js";
import { apiErrorMessage } from "../lib/apiErrorMessage.js";
import { siteInfo } from "../lib/siteInfo.js";
import { AutoTextarea } from "./AutoTextarea.js";

type ReasonOption = { value: ReportReason; labelKey: TranslationKey };

/** Reasons in two groups: harmful content, then rights / spam / anything else. */
const REASON_GROUPS: { headingKey: TranslationKey; reasons: ReasonOption[] }[] = [
  {
    headingKey: "report.groupHarmful",
    reasons: [
      { value: "sexual", labelKey: "report.reason.sexual" },
      { value: "minors", labelKey: "report.reason.minors" },
      { value: "violence", labelKey: "report.reason.violence" },
      { value: "hate", labelKey: "report.reason.hate" }
    ]
  },
  {
    headingKey: "report.groupOther",
    reasons: [
      { value: "real_person", labelKey: "report.reason.realPerson" },
      { value: "copyright", labelKey: "report.reason.copyright" },
      { value: "spam", labelKey: "report.reason.spam" },
      { value: "other", labelKey: "report.reason.other" }
    ]
  }
];

/**
 * "Report" dialog for a novel, a comment or an author profile. Open to anyone,
 * signed in or not — a rights holder may not have an account. The report lands
 * in the admin dashboard (and the team's Discord) for review and takedown.
 * Portaled to document.body like the other modals.
 */
export function ReportDialog({
  targetType,
  targetId,
  novelId,
  onClose
}: {
  targetType: ReportTargetType;
  targetId: string;
  novelId?: string;
  onClose: () => void;
}) {
  const t = useT();
  const dialogRef = useRef<HTMLDivElement>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const pointerDownOnBackdrop = useRef(false);
  const [reason, setReason] = useState<ReportReason | null>(null);
  const [details, setDetails] = useState("");
  const [contact, setContact] = useState("");
  const [reporterName, setReporterName] = useState("");
  const [goodFaith, setGoodFaith] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const previouslyFocused = document.activeElement as HTMLElement | null;
    dialogRef.current?.focus();
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.stopPropagation();
        onCloseRef.current();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      previouslyFocused?.focus?.();
    };
  }, []);

  async function send(event?: FormEvent) {
    event?.preventDefault();
    if (!reason) {
      setError(t("report.pickReason"));
      return;
    }
    if (!goodFaith) {
      setError(t("report.goodFaithRequired"));
      return;
    }
    setError(null);
    try {
      await submitReport({ targetType, targetId, novelId, reason, details, contact, reporterName });
      setSent(true);
    } catch (err) {
      // The daily cap per network (429) closes this form, not the DSA art. 16
      // notice path: point at the address the Terms give for reporting by email.
      const supportEmail = siteInfo.supportEmail;
      const hasEmail = supportEmail.includes("@") && !supportEmail.startsWith("[");
      const limited = (err as { status?: unknown } | null)?.status === 429;
      setError(
        limited && hasEmail
          ? t("report.rateLimited", { email: supportEmail })
          : apiErrorMessage(err, t, t("report.failed"))
      );
    }
  }

  return createPortal(
    <div
      className="admin-modal-backdrop"
      onMouseDown={(event) => {
        pointerDownOnBackdrop.current = event.target === event.currentTarget;
      }}
      onClick={(event) => {
        if (event.target === event.currentTarget && pointerDownOnBackdrop.current) onClose();
      }}
    >
      <div
        ref={dialogRef}
        tabIndex={-1}
        className="admin-modal report-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="report-dialog-title"
        onClick={(event) => event.stopPropagation()}
      >
        <button type="button" className="admin-modal-close" aria-label={t("popup.close")} onClick={onClose}>
          ×
        </button>
        <h2 id="report-dialog-title">{t(`report.title.${targetType}` as TranslationKey)}</h2>
        {sent ? (
          <>
            <p className="report-sent" role="status">
              {t("report.sent")}
            </p>
            <div className="button-row">
              <button type="button" onClick={onClose}>
                {t("popup.close")}
              </button>
            </div>
          </>
        ) : (
          <form className="report-form" onSubmit={send}>
            <fieldset>
              <legend>{t("report.reasonLegend")}</legend>
              {REASON_GROUPS.map((group) => (
                <div key={group.headingKey} className="report-reason-group">
                  <p className="report-reason-heading">{t(group.headingKey)}</p>
                  {group.reasons.map((option) => (
                    <label
                      key={option.value}
                      className={`report-reason${reason === option.value ? " is-selected" : ""}`}
                    >
                      <input
                        type="radio"
                        name="report-reason"
                        value={option.value}
                        checked={reason === option.value}
                        onChange={() => setReason(option.value)}
                      />
                      {t(option.labelKey)}
                    </label>
                  ))}
                </div>
              ))}
            </fieldset>
            <label className="report-field">
              {t("report.details")}
              <AutoTextarea value={details} maxLength={2000} rows={3} onChange={(e) => setDetails(e.target.value)} />
            </label>
            <label className="report-field">
              {t("report.name")}
              <input value={reporterName} maxLength={100} onChange={(e) => setReporterName(e.target.value)} />
            </label>
            <label className="report-field">
              {t("report.contact")}
              <input value={contact} maxLength={200} onChange={(e) => setContact(e.target.value)} />
            </label>
            <label className="report-good-faith">
              <input type="checkbox" checked={goodFaith} onChange={(e) => setGoodFaith(e.target.checked)} />
              <span>{t("report.goodFaith")}</span>
            </label>
            {error && (
              <p className="auth-error" role="alert">
                {error}
              </p>
            )}
            <div className="button-row">
              <AsyncButton type="button" onClick={() => send()} loadingLabel={t("report.sending")}>
                {t("report.submit")}
              </AsyncButton>
              <button type="button" className="secondary" onClick={onClose}>
                {t("report.cancel")}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>,
    document.body
  );
}
